import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ChurchCombobox } from '@/components/church-combobox';
import { PlatformSupportTicketDetailDialog } from '@/components/platform-support-ticket-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { api, type OffsetPage } from '@/lib/api';
import {
  formatTicketNumber,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  type SupportTicketSummary,
  isFirstReplyOverdue,
  isResolutionOverdue,
} from '@/lib/support-ticket-types';
import { formatDateTime } from '@/utils/format';

const statusVariant = (status: SupportTicketSummary['status']): BadgeVariant => {
  if (status === 'escalated') return 'danger';
  if (status === 'awaiting_platform') return 'warning';
  if (status === 'resolved') return 'success';
  if (status === 'closed') return 'neutral';
  return 'info';
};

const priorityVariant = (priority: SupportTicketSummary['priority']): BadgeVariant => {
  if (priority === 'urgent') return 'danger';
  if (priority === 'high') return 'warning';
  if (priority === 'low') return 'neutral';
  return 'info';
};

export const SupportTicketsPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const selectedTicketId = searchParams.get('ticketId');

  const churchId = searchParams.get('churchId') ?? '';
  const status = searchParams.get('status') ?? '';
  const priority = searchParams.get('priority') ?? '';
  const assignedPlatformUserId = searchParams.get('assignedPlatformUserId') ?? '';
  const unassigned = searchParams.get('unassigned') === 'true';
  const escalatedOnly = searchParams.get('escalatedOnly') === 'true';
  const slaBreached = searchParams.get('slaBreached') === 'true';
  const q = searchParams.get('q') ?? '';

  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const setFlag = (key: string, enabled: boolean) => {
    setFilter(key, enabled ? 'true' : null);
  };

  const staff = useQuery({
    queryKey: ['platform-support-staff'],
    queryFn: async () => {
      const result = await api.get<{
        data: Array<{
          id: string;
          firstName: string;
          lastName: string;
          platformRole: string;
        }>;
      }>('/platform/users', {
        query: {
          page: 1,
          pageSize: 100,
          platformRole: 'platform_admin,platform_manager',
          status: 'active',
          sort: 'lastName',
        },
      });
      return result.data.filter((row) => row.platformRole !== 'none');
    },
  });

  const list = useQuery({
    queryKey: [
      'platform-support-tickets',
      page,
      pageSize,
      churchId,
      status,
      priority,
      assignedPlatformUserId,
      unassigned,
      escalatedOnly,
      slaBreached,
      q,
    ],
    queryFn: () =>
      api.get<{ data: SupportTicketSummary[]; page: OffsetPage }>('/platform/support/tickets', {
        query: {
          page,
          pageSize,
          sort: '-lastMessageAt',
          ...(churchId ? { churchId } : {}),
          ...(status ? { status } : {}),
          ...(priority ? { priority } : {}),
          ...(assignedPlatformUserId ? { assignedPlatformUserId } : {}),
          ...(unassigned ? { unassigned: 'true' } : {}),
          ...(escalatedOnly ? { escalatedOnly: 'true' } : {}),
          ...(slaBreached ? { slaBreached: 'true' } : {}),
          ...(q ? { q } : {}),
        },
      }),
  });

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('supportTickets.filters.allStatuses') },
      ...SUPPORT_TICKET_STATUSES.map((value) => ({
        value,
        label: t(`supportTickets.statuses.${value}`),
      })),
    ],
    [t],
  );

  const priorityOptions = useMemo(
    () => [
      { value: '', label: t('supportTickets.filters.allPriorities') },
      ...SUPPORT_TICKET_PRIORITIES.map((value) => ({
        value,
        label: t(`supportTickets.priorities.${value}`),
      })),
    ],
    [t],
  );

  const assigneeOptions = useMemo(
    () => [
      { value: '', label: t('supportTickets.filters.allAssignees') },
      { value: '__unassigned__', label: t('supportTickets.filters.unassignedOnly') },
      ...(staff.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })),
    ],
    [staff.data, t],
  );

  const assigneeFilterValue = unassigned
    ? '__unassigned__'
    : assignedPlatformUserId || '';

  const columns = useMemo((): DataTableColumn<SupportTicketSummary>[] => {
    return [
      {
        id: 'lastMessageAt',
        header: t('supportTickets.columns.lastActivity'),
        cell: (row) => formatDateTime(row.lastMessageAt),
      },
      {
        id: 'number',
        header: t('supportTickets.columns.number'),
        width: '7rem',
        cell: (row) => (
          <span className="font-medium tabular-nums">{formatTicketNumber(row.number)}</span>
        ),
      },
      {
        id: 'subject',
        header: t('supportTickets.columns.subject'),
        cell: (row) => <span className="font-medium">{row.subject}</span>,
      },
      {
        id: 'church',
        header: t('supportTickets.columns.church'),
        cell: (row) => row.churchName ?? t('audit.emptyValue'),
        hideBelowMd: true,
      },
      {
        id: 'status',
        header: t('supportTickets.columns.status'),
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <Badge variant={statusVariant(row.status)} className="w-fit self-start">
              {t(`supportTickets.statuses.${row.status}`)}
            </Badge>
            <div className="flex flex-wrap gap-1">
              {row.escalationLevel > 0 || row.status === 'escalated' ? (
                <Badge variant="warning">{t('supportTickets.badges.escalated')}</Badge>
              ) : null}
              {isFirstReplyOverdue(row) ? (
                <Badge variant="danger">{t('supportTickets.badges.firstReplyOverdue')}</Badge>
              ) : null}
              {isResolutionOverdue(row) ? (
                <Badge variant="danger">{t('supportTickets.badges.resolutionOverdue')}</Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'priority',
        header: t('supportTickets.columns.priority'),
        cell: (row) => (
          <Badge variant={priorityVariant(row.priority)}>
            {t(`supportTickets.priorities.${row.priority}`)}
          </Badge>
        ),
        hideBelowMd: true,
      },
      {
        id: 'assignee',
        header: t('supportTickets.columns.assignee'),
        cell: (row) => row.assignedPlatformName ?? t('supportTickets.detail.unassigned'),
        hideBelowMd: true,
      },
    ];
  }, [t]);

  const pageMeta = list.data?.page;
  const rows = list.data?.data ?? [];

  const openTicket = (ticketId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('ticketId', ticketId);
    setSearchParams(next, { replace: true });
  };

  const closeTicket = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('ticketId');
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader title={t('supportTickets.title')} description={t('supportTickets.description')} />

      <div className="mb-6 flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <FormField label={t('supportTickets.columns.church')}>
          {(field) => (
            <ChurchCombobox
              {...field}
              value={churchId || null}
              onChange={(value) => setFilter('churchId', value)}
              placeholder={t('supportTickets.filters.churchPlaceholder')}
              emptyMessage={t('supportTickets.filters.churchEmpty')}
              clearLabel={t('supportTickets.filters.clearChurch')}
            />
          )}
        </FormField>
        <FormField label={t('supportTickets.columns.status')}>
          {(field) => (
            <Listbox
              {...field}
              value={status}
              onChange={(value) => setFilter('status', value || null)}
              options={statusOptions}
            />
          )}
        </FormField>
        <FormField label={t('supportTickets.columns.priority')}>
          {(field) => (
            <Listbox
              {...field}
              value={priority}
              onChange={(value) => setFilter('priority', value || null)}
              options={priorityOptions}
            />
          )}
        </FormField>
        <FormField label={t('supportTickets.columns.assignee')}>
          {(field) => (
            <Listbox
              {...field}
              value={assigneeFilterValue}
              onChange={(value) => {
                if (value === '__unassigned__') {
                  const next = new URLSearchParams(searchParams);
                  next.delete('assignedPlatformUserId');
                  next.set('unassigned', 'true');
                  setSearchParams(next, { replace: true });
                  setPage(1);
                  return;
                }
                const next = new URLSearchParams(searchParams);
                next.delete('unassigned');
                if (value) next.set('assignedPlatformUserId', value);
                else next.delete('assignedPlatformUserId');
                setSearchParams(next, { replace: true });
                setPage(1);
              }}
              options={assigneeOptions}
            />
          )}
        </FormField>
      </div>
      <div className="grid items-end gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <FormField label={t('supportTickets.filters.search')} className="sm:col-span-2">
          {(field) => (
            <div className="flex gap-2">
              <Input
                {...field}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t('supportTickets.filters.searchPlaceholder')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setFilter('q', searchInput.trim() || null);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => setFilter('q', searchInput.trim() || null)}
              >
                {t('supportTickets.filters.searchAction')}
              </Button>
            </div>
          )}
        </FormField>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:col-span-2 sm:pb-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-escalated"
              checked={escalatedOnly}
              onCheckedChange={(value) => setFlag('escalatedOnly', value === true)}
            />
            <Label htmlFor="filter-escalated" className="text-body-sm font-normal">
              {t('supportTickets.filters.escalatedOnly')}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-overdue"
              checked={slaBreached}
              onCheckedChange={(value) => setFlag('slaBreached', value === true)}
            />
            <Label htmlFor="filter-overdue" className="text-body-sm font-normal">
              {t('supportTickets.filters.overdueOnly')}
            </Label>
          </div>
        </div>
      </div>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => openTicket(row.id)}
        empty={
          <p className="py-8 text-center text-body-sm text-fg-secondary">{t('supportTickets.empty')}</p>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <PlatformSupportTicketDetailDialog
        ticketId={selectedTicketId}
        open={Boolean(selectedTicketId)}
        onOpenChange={(next) => {
          if (!next) closeTicket();
        }}
      />
    </div>
  );
};
